import { CloseOutlined, PaperClipOutlined } from '@ant-design/icons';
import { Button, Popover, Tooltip, Typography } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';

import type { LeadMessageAttachment } from '@app/components/LeadCard/ui/MessageComposer/types';
import { formatAttachmentSize } from '@app/components/LeadCard/ui/MessageComposer/utils';

import type { FC } from 'react';

import './LeadMessageAttachments.scss';

const MIN_POPOVER_WIDTH = 180;
const POPOVER_VIEWPORT_OFFSET = 16;

interface LeadMessageAttachmentsProps {
  attachments: LeadMessageAttachment[];
  disabled?: boolean;
  onRemoveAttachment(attachmentId: string): void;
}

interface AttachmentChipProps {
  attachment: LeadMessageAttachment;
  className?: string;
  disabled?: boolean;
  onRemoveAttachment(attachmentId: string): void;
}

const getAttachmentsPopupContainer = (triggerNode: HTMLElement): HTMLElement =>
  triggerNode.closest<HTMLElement>('.LeadCard') ?? document.body;

const getFilePlural = (count: number): string => {
  const normalizedCount = Math.abs(count);
  const lastTwoDigits = normalizedCount % 100;
  const lastDigit = normalizedCount % 10;

  if (lastTwoDigits >= 11 && lastTwoDigits <= 14) return 'файлов';
  if (lastDigit === 1) return 'файл';
  if (lastDigit >= 2 && lastDigit <= 4) return 'файла';

  return 'файлов';
};

const getOverflowLabel = (count: number): string =>
  `ещё ${count} ${getFilePlural(count)}`;

const AttachmentChip: FC<AttachmentChipProps> = ({
  attachment,
  className,
  disabled,
  onRemoveAttachment,
}) => {
  const size = formatAttachmentSize(attachment.FileSize);
  const itemClassName = ['LeadMessageAttachments__item', className]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={itemClassName}>
      <PaperClipOutlined className="LeadMessageAttachments__icon" />
      <Typography.Text
        className="LeadMessageAttachments__name"
        title={attachment.FileName ?? undefined}
        ellipsis
      >
        {attachment.FileName || 'Файл'}
      </Typography.Text>
      {size && (
        <Typography.Text
          className="LeadMessageAttachments__size"
          type="secondary"
        >
          {size}
        </Typography.Text>
      )}
      <Tooltip title="Убрать файл">
        <Button
          aria-label="Убрать файл"
          className="LeadMessageAttachments__remove"
          disabled={disabled}
          icon={<CloseOutlined />}
          size="small"
          type="text"
          onClick={() => onRemoveAttachment(attachment.id)}
        />
      </Tooltip>
    </div>
  );
};

export const LeadMessageAttachments: FC<LeadMessageAttachmentsProps> = ({
  attachments,
  disabled,
  onRemoveAttachment,
}) => {
  const rowRef = useRef<HTMLDivElement>(null);
  const [rowWidth, setRowWidth] = useState(0);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const visibleAttachments = useMemo(
    () => attachments.slice(0, 1),
    [attachments]
  );
  const hiddenAttachments = useMemo(() => attachments.slice(1), [attachments]);
  const hiddenCount = hiddenAttachments.length;
  const overflowLabel = getOverflowLabel(hiddenCount);

  useEffect(() => {
    if (hiddenCount === 0) {
      setIsPopoverOpen(false);
    }
  }, [hiddenCount]);

  const popoverWidth = useMemo(() => {
    const measuredWidth = Math.floor(
      rowRef.current?.getBoundingClientRect().width ?? 0
    );
    const nextRowWidth = measuredWidth || rowWidth;
    if (nextRowWidth <= 0) return MIN_POPOVER_WIDTH;

    const maxViewportWidth =
      typeof window === 'undefined'
        ? nextRowWidth
        : window.innerWidth - POPOVER_VIEWPORT_OFFSET;

    return Math.max(
      MIN_POPOVER_WIDTH,
      Math.min(nextRowWidth, maxViewportWidth)
    );
  }, [rowWidth]);

  useEffect(() => {
    const rowElement = rowRef.current;
    if (!rowElement) return undefined;

    const updateRowWidth = () => {
      const nextWidth = Math.floor(rowElement.getBoundingClientRect().width);

      setRowWidth((prevWidth) =>
        prevWidth === nextWidth ? prevWidth : nextWidth
      );
    };

    updateRowWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateRowWidth);

      return () => {
        window.removeEventListener('resize', updateRowWidth);
      };
    }

    const resizeObserver = new ResizeObserver(updateRowWidth);
    resizeObserver.observe(rowElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  if (attachments.length === 0) return null;

  const popoverContent = (
    <div className="LeadMessageAttachments__popover-list">
      {hiddenAttachments.map((attachment) => (
        <AttachmentChip
          attachment={attachment}
          disabled={disabled}
          key={attachment.id}
          onRemoveAttachment={onRemoveAttachment}
        />
      ))}
    </div>
  );

  return (
    <div className="LeadMessageAttachments">
      <div
        className={[
          'LeadMessageAttachments__row',
          hiddenCount > 0 && 'LeadMessageAttachments__row--with-overflow',
        ]
          .filter(Boolean)
          .join(' ')}
        ref={rowRef}
      >
        {visibleAttachments.map((attachment) => (
          <AttachmentChip
            attachment={attachment}
            disabled={disabled}
            key={attachment.id}
            onRemoveAttachment={onRemoveAttachment}
          />
        ))}
        {hiddenCount > 0 && (
          <Popover
            arrow={false}
            classNames={{
              body: 'LeadMessageAttachments__popover-body',
              root: 'LeadMessageAttachments__popover',
            }}
            content={popoverContent}
            getPopupContainer={getAttachmentsPopupContainer}
            open={isPopoverOpen}
            placement="topRight"
            styles={{
              root: {
                maxWidth: `calc(100vw - ${POPOVER_VIEWPORT_OFFSET}px)`,
                width: popoverWidth,
              },
            }}
            trigger="click"
            onOpenChange={setIsPopoverOpen}
          >
            <Button
              className="LeadMessageAttachments__overflow"
              size="small"
              type="text"
            >
              {overflowLabel}
            </Button>
          </Popover>
        )}
      </div>
    </div>
  );
};
